import React, {Component} from "react"
import PropTypes from "prop-types"
import {cloneDeep, get} from "lodash"
import styled from "styled-components"
import {connect} from "react-redux"

import padding from "@irati/ui/src/global/padding"
import getId from "@irati/utils/id"
import Loading from "@irati/ui/src/Loading"
import {FormattedMessage, injectIntl} from "@irati/utils/lang"
import authentication from "@irati/auth"
import {STATUS_SCHEDULED} from "@irati/insights/constants/survey"

import Loader from "@irati/ui/src/Loading/Loader"
import {IN_PROGRESS, LAUNCHED} from "@irati/insights/constants/schedule"
import Button from "@irati/ui/src/Button"
import {DemoTenantContext} from "@irati/insights/context"
import {hangPromise} from "@irati/utils/promise"
import {overrideAuth} from "@irati/insights/utils/apollo"

import {getSurveys} from "../../graph/surveys"
import {DateCellRenderer, PB_LAST_COLUMN, PB_PROP_CENTER, PB_PROP_RIGHT, ReactTable, applyPbPadding} from "../../ui/Table"
import {TeamsCellRenderer, SurveyResponseRenderer} from "../Renderers"
import IconCellRenderer from "./renderers/IconCellRenderer"

const DATE_PROPERTY = "openedAt"
const INITIAL_SURVEY_COUNT = 2
const SURVEY_COUNT_LIMIT = 100

const TableWrapper = styled.div`
  padding: ${padding.small} ${padding.small} ${padding.small} 0;
`

const columns = (formatMessage, rowCount) => [
  {
    Header: null,
    accessor: "status",
    Cell: (props) => <IconCellRenderer {...props} rowCount={rowCount} />,
    sortable: false,
    width: 49,
    style: {padding: "0", paddingRight: padding.small, boxShadow: "0 1px 0 0 white"}
  },
  {
    Header: formatMessage({id: "insights.cycleList.column.status"}),
    accessor: "status",
    Cell: ({original}) => <FormattedMessage {...original} id={`insights.survey.status.${original.status}`} />,
    sortable: false
  },
  {
    Header: formatMessage({id: "insights.cycleList.column.date"}),
    accessor: DATE_PROPERTY,
    Cell: (props) => <DateCellRenderer {...props} property="openedAt" />,
    [PB_PROP_RIGHT]: true,
    minWidth: 120,
    sortable: false
  },
  {
    Header: formatMessage({id: "insights.cycleList.column.responses"}),
    Cell: (props) => <SurveyResponseRenderer {...props} />,
    [PB_PROP_RIGHT]: true,
    minWidth: 140,
    sortable: false
  },
  {
    Header: formatMessage({id: "insights.cycleList.column.responseRate"}),
    [PB_PROP_RIGHT]: true,
    Cell: ({original}) => {
      const {questionStats, responseCount, sendCount} = original
      return questionStats ? `${Math.round((responseCount / sendCount) * 100) || 0}%` : "--"
    },
    sortable: false
  },
  {
    Header: formatMessage({id: "insights.cycleList.column.comments"}),
    accessor: "commentCount",
    [PB_PROP_RIGHT]: true,
    sortable: false
  },
  {
    Header: formatMessage({id: "insights.cycleList.column.teams"}),
    accessor: "preferences",
    Cell: (props) => <TeamsCellRenderer {...props} />,
    [PB_LAST_COLUMN]: true,
    minWidth: 150,
    sortable: false
  }
]

const applyFlexPbTextAlignment = (col) => {
  const style = {}
  if (!col) return style
  if (col[PB_PROP_CENTER]) style.justifyContent = "center"
  if (col[PB_PROP_RIGHT]) style.justifyContent = "flex-end"
  if (!style.justifyContent) style.justifyContent = "flex-start"
  return style
}

const getTdProps = (state, row, col) => {
  return {
    style: {
      ...applyPbPadding(row, col, false),
      ...applyFlexPbTextAlignment(col),
      display: "flex",
      alignItems: "center",
      border: "none"
    }
  }
}

class SurveyList extends Component {
  static propTypes = {
    schedule: PropTypes.shape({
      id: PropTypes.string.isRequired,
      reference: PropTypes.string.isRequired,
      nextTriggerAt: PropTypes.string.isRequired,
      preferences: PropTypes.shape({
        sendToAll: PropTypes.bool.isRequired,
        teams: PropTypes.object.isRequired
      })
    }).isRequired,
    formatMessage: PropTypes.func.isRequired,
    isPhone: PropTypes.bool.isRequired
  }

  baseSurveyInput = {
    orderBy: "openedAt",
    orderDirection: "desc",
    withQuestionStats: true,
    withResponseCount: true,
    test: 0
  }

  constructor(props) {
    super(props)
    this.state = {
      initialLoad: hangPromise(),
      listLoading: false,
      allSurveysLoaded: false,
      isExpanded: false,
      surveyList: [],
      totalSurveys: 0
    }

    this.waitingForInitialTrigger = true
  }

  getTenantAndcycleRequestParams = (insightsDemoTenant) => {
    const {accessToken, cycleUuid, tenantUuid} = insightsDemoTenant
    const propSchduleId = get(this.props, "schedule.id")
    const isDemo = propSchduleId === cycleUuid
    const scheduleId = isDemo ? cycleUuid : propSchduleId
    const tenant = isDemo ? tenantUuid : authentication.userTenant().uuid

    const params = {
      tenant,
      scheduleId
    }

    let options
    if (isDemo) {
      options = overrideAuth(accessToken).context
    }

    return {
      params,
      options
    }
  }

  fetchInitialSurveys = (insightsDemoTenant) => {
    const {params: tenantCycleParams, options} = this.getTenantAndcycleRequestParams(insightsDemoTenant)

    const params = {
      ...this.baseSurveyInput,
      ...tenantCycleParams,
      limit: INITIAL_SURVEY_COUNT
    }

    const initialLoad = getSurveys(params, options)
      .then(({results, total}) => {
        const surveyList = [...this.getNextScheduledSurvey(), ...results]
        this.setState({surveyList: surveyList.map((survey) => ({...survey, isInitial: true})), totalSurveys: total})
      })
      .catch(() => this.setState({listLoading: false}))

    this.setState({initialLoad})
    return initialLoad
  }

  fetchAllSurveys = (insightsDemoTenant) => {
    this.setState({listLoading: true})
    const {params: tenantCycleParams, options} = this.getTenantAndcycleRequestParams(insightsDemoTenant)

    const params = {
      ...this.baseSurveyInput,
      ...tenantCycleParams,
      limit: SURVEY_COUNT_LIMIT,
      offset: INITIAL_SURVEY_COUNT
    }
    getSurveys(params, options)
      .then(({results, total}) => {
        const surveyList = [...this.state.surveyList, ...results]
        this.setState({surveyList, allSurveysLoaded: true, listLoading: false, isExpanded: true, totalSurveys: total})
      })
      .catch(() => this.setState({listLoading: false}))
  }

  handleLoadMore = (insightsDemoTenant) => {
    const {allSurveysLoaded, isExpanded} = this.state

    if (isExpanded) return this.setState({isExpanded: false})

    if (allSurveysLoaded) return this.setState({isExpanded: true})

    this.fetchAllSurveys(insightsDemoTenant)
  }

  getNextScheduledSurvey = () => {
    const {schedule} = this.props

    if (![LAUNCHED, IN_PROGRESS].includes(schedule.validationStatus)) return []

    const {id, reference, nextTriggerAt, preferences} = schedule

    return [
      {
        id,
        reference,
        openedAt: nextTriggerAt,
        createdAt: nextTriggerAt,
        status: STATUS_SCHEDULED,
        responseCount: 0,
        commentCount: "--",
        preferences: {
          sendToAll: preferences.sendToAll,
          teams: cloneDeep(preferences.teams)
        }
      }
    ]
  }

  render() {
    return (
      <DemoTenantContext.Consumer>
        {(insightsDemoTenant) => {
          /** Initial load moved inside context */
          if (this.waitingForInitialTrigger) {
            this.waitingForInitialTrigger = false
            this.fetchInitialSurveys(insightsDemoTenant)
          }

          const idNamespace = getId("survey-list", this.props, {force: true})
          const {surveyList, listLoading, initialLoad, isExpanded, totalSurveys} = this.state
          const {formatMessage} = this.props
          const shouldShowMore = surveyList.length >= INITIAL_SURVEY_COUNT && totalSurveys > INITIAL_SURVEY_COUNT && !listLoading
          const showMoreText = isExpanded
            ? "insights.homepage.surveyList.expander.showLess"
            : "insights.homepage.surveyList.expander.showMore"
          const data = isExpanded ? surveyList : surveyList.filter((survey) => survey.isInitial)
          return (
            <TableWrapper id={idNamespace}>
              <Loading load={initialLoad}>
                <ReactTable
                  id={getId("SurveyList", idNamespace)}
                  data={data}
                  showPagination={false}
                  getTdProps={(state, row, col) => getTdProps(state, row, col)}
                  columns={columns(formatMessage, surveyList.length)} />
              </Loading>
              {listLoading && <Loader />}
              {shouldShowMore && (
                <Button tertiary onClick={() => this.handleLoadMore(insightsDemoTenant)}>
                  {" "}
                  <FormattedMessage id={showMoreText} />
                </Button>
              )}
            </TableWrapper>
          )
        }}
      </DemoTenantContext.Consumer>
    )
  }
}

const mapStateToProps = ({mq}) => ({
  isPhone: mq.lessThan.medium
})

export default injectIntl(connect(mapStateToProps)(SurveyList))
